/**
 * Offers panel — pure preparation logic.
 *
 * The product page is moving from ONE buy link to a split "Buy Now" pill whose
 * right zone opens a panel listing several retailers with verified prices per
 * country. Everything in this file is deterministic and framework-free: no
 * React, no 'use client', no DOM. Both the desktop morph panel and the mobile
 * bottom sheet (Wave 2) consume the same prepared rows, so the ordering, the
 * badges and the "how many are hidden" arithmetic can only be wrong in one
 * place.
 *
 * The dark launch is STRUCTURAL, not flagged: callers render the offers branch
 * only when `countOfferRows(prepareOffers(...).rows) >= 2`. A product with
 * zero or one surviving priced offer (any number of information links) falls
 * through to today's single-link rendering untouched.
 *
 * Offers Engine v2 (docs/OFFERS_ENGINE_V2.md section 6) adds, all computed
 * here so every surface agrees: the "as of" stamps (6.1), the honesty badge
 * against the 60-day typical price and the was-price it can suppress (6.2),
 * information links and the price kinds (6.4, 6.5), and the opaque tracking
 * token every prepared row carries to the /go href (5.3).
 */

import { AmazonDomain } from 'shared/src/types/amazon.type';
import type { Offer as CanonicalOffer } from 'shared/src/types/product.type';
import { RegionCode } from 'shared/src/types/region.type';
import { isDtcStoreUrl } from 'shared/src/data/purchase-channels';
import { buyLinkProblem, isMakerLink, linkSellsInMarket, offerStoreEvidence } from 'shared/src/utils/buy-link-market';
import { purchaseChannel, type PurchaseChannel } from 'shared/src/utils/purchase-channel';
import { getDisplayDomain, getRetailerName } from 'shared/src/utils/retailer-name';
import {
  isLabelIneligibleKind,
  lookupOfferMerchant,
  offerPresentationTier,
  resolveOfferConfidence,
} from 'shared/src/utils/offers/rank-offers';
import { getCurrencyCode, priceByCountry } from '@/lib/ui-content/price-ui-content';

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

/** Amount + ISO-4217 code, in MAJOR units (the backend stores integer cents
 *  and converts at the boundary, so `12.5` here means $12.50, never 12.5¢). */
export type OfferPrice = NonNullable<CanonicalOffer['price']>;

export type OfferAvailability = NonNullable<CanonicalOffer['availability']>;

// Derived, not restated: a hand-written copy of this union silently stopped
// accepting rows the moment the schema learned a new writer ('feed',
// 'brand-site'), and the mismatch only surfaced at the call sites that pass
// offers in from the wire.
export type OfferSource = CanonicalOffer['source'];
export type OfferMerchantKind = NonNullable<CanonicalOffer['merchantKind']>;
export type OfferPriceKind = NonNullable<CanonicalOffer['priceKind']>;
export type OfferDeal = NonNullable<CanonicalOffer['deal']>;

/**
 * What a row actually links to.
 *
 * 'offer' — a verified listing for THIS product at THIS merchant, with a price
 * we observed (or a list / member / hidden price, see `priceKind`).
 * 'search' — a search results page at the merchant, no price and no promise
 * that the exact product is on the other side. Only `prepareLinkOffers` and
 * the out-of-stock Amazon fallback produce these. The row renders them
 * differently on purpose: a search is not an offer, and a panel that dressed
 * one up as the other would be lying in the one place a reader is deciding
 * where to spend money.
 * 'info' — a brand's own product page for a brand that does not sell online
 * in this market (a `brand-site` row with no price, spec 6.4.6). Listed last,
 * never counted, never badged, never the pick, still routed through /go.
 */
export type OfferKind = 'offer' | 'search' | 'info';

/**
 * The subset of the canonical `Offer` this panel renders. Unions come from the
 * schema above, `kind` included: an incoming row is an 'offer' or an 'info'
 * link, exactly as `product_offers` can store it. Only a PREPARED row can be a
 * 'search' (see `PreparedOffer`), which keeps the two directions assignable:
 * every canonical Offer and every SearchOfferPreview is a ProductOffer, and
 * every ProductOffer is a canonical Offer (the realtime merge hands them back).
 */
export type ProductOffer = {
  /** `product_offers.id`, carried to /go as `o=`. */
  offerId?: string;
  merchantDomain: string;
  merchantName?: string;
  merchantKind?: OfferMerchantKind;
  url: string;
  /** `product_offers.tracking_url_enc`: opaque ciphertext, copied to the /go
   *  href as `t=` and never parsed here (spec 5.3). */
  trackingToken?: string;
  price?: OfferPrice;
  originalPrice?: OfferPrice;
  priceKind?: OfferPriceKind;
  kind?: CanonicalOffer['kind'];
  availability?: OfferAvailability;
  source: OfferSource;
  /** Nullable to match `Offer.confidence` on the wire — the Cursor pipeline and
   *  the engine API both emit `null`. Always read via `resolveOfferConfidence`. */
  confidence?: number | null;
  isBestPrice?: boolean;
  isBestPlace?: boolean;
  /** ISO-8601 timestamp of the last successful price verification. */
  verifiedAt?: string;
  /** ISO-8601 timestamp the SOURCE put on the price; drives the "as of" stamp. */
  observedAt?: string;
  /** The 60-day median (spec 6.2), present only from 20 daily points. */
  typicalPrice?: OfferPrice;
  typicalPricePoints?: number;
  deal?: OfferDeal;
  infoReason?: string;
  why?: string;
  attributionRequired?: boolean;
};

export type OfferBadge = 'bestPlace' | 'bestPrice';

/** Rows we actually track as listings — search-results rows and information
 *  links do not count: neither is a price we observed at a store. */
export const countTrackedRetailers = (
  rows: ReadonlyArray<{ kind?: OfferKind }>,
): number => rows.filter((row) => row.kind !== 'search' && row.kind !== 'info').length;

/** The structural threshold's count (spec 6.4.5): every row that is not an
 *  information link. Two of these make a comparison; any number of
 *  information links do not. */
export const countOfferRows = (rows: ReadonlyArray<{ kind?: OfferKind }>): number =>
  rows.filter((row) => row.kind !== 'info').length;

export const isInfoOffer = (offer: { kind?: OfferKind } | null | undefined): boolean =>
  offer?.kind === 'info';

/** An offer, filtered/sorted/annotated and ready to render. */
export type PreparedOffer = Omit<ProductOffer, 'kind'> & {
  /** Lower-cased, `www.`-stripped merchantDomain — the dedupe key and the
   *  favicon lookup key. */
  domainKey: string;
  /** Defaults to 'offer' — see `OfferKind`. */
  kind?: OfferKind;
  /** At most ONE row carries 'bestPlace' and at most one carries 'bestPrice'. */
  badge: OfferBadge | null;
  /** Confidence below CONFIDENCE_APPROX_THRESHOLD ⇒ render the price with the
   *  localized "around {price}" hedge instead of asserting it. */
  isApproximate: boolean;
  /** Localized price string, e.g. "$129.99" / "1 299,00 kr" / "¥12,800". */
  priceLabel: string;
  /** Localized was-price, rendered line-through. '' when there is no discount,
   *  and '' when the typical price says the was-price is not credible (6.2.5). */
  originalPriceLabel: string;
  /** originalPrice − price in major units, when strictly positive. */
  saveAmount?: number;
  /** Rounded percentage off, when strictly positive. */
  savePercent?: number;
  /** Localized `saveAmount`. '' when there is no discount. */
  saveLabel: string;
  /** The honesty badge's number (spec 6.2): present only when the row earns
   *  it, and then never below BELOW_USUAL_MIN_PERCENT. */
  belowUsualPercent?: number;
  /** Localized typical price for the badge's tooltip. '' when none. */
  typicalPriceLabel: string;
};

export type PrepareOffersOptions = {
  /** The page's market. Drives BOTH the currency filter and every price format;
   *  always the locale's region (URL-derived), never IP geolocation. */
  regionCode: RegionCode;
  /** The page's locale path ("fr-CA", "sv-SE"), so every price label is
   *  written the way that locale writes money ("26,27 $" rather than
   *  "$26.27"). Omitted, labels keep the en-US-grouped format. */
  localePath?: string;
  /** App-shell / regulated surfaces where Amazon rows must not be shown. When
   *  this drops the list below 2 rows the caller falls back to the legacy path. */
  suppressAmazon?: boolean;
};

export type PreparedOffers = {
  /** Every surviving offer, sorted. C2 renders `rows.slice(0, MAX_VISIBLE_ROWS)`
   *  until the "Show all {n}" expander is used. */
  rows: PreparedOffer[];
  /** How many rows fall OUTSIDE the initial visible window
   *  (`max(0, rows.length - MAX_VISIBLE_ROWS)`). Drives the expander; it is NOT
   *  a count of offers dropped by filtering. */
  hiddenCount: number;
  /** bestPlace ?? Amazon ?? cheapest in-stock ?? first, never an information
   *  link. `null` when empty. */
  primary: PreparedOffer | null;
  /** Newest observation across the non-info `rows`, verbatim. `null` when none parse. */
  freshestObservedAt: string | null;
  /** OLDEST observation across the non-info `rows`, verbatim. This is the one
   *  the panel's "Prices checked {time}" claim uses: the freshness claim must
   *  be true of EVERY row shown, so it is bounded by the stalest one. Reads the
   *  source's own `observedAt`, falling back to `verifiedAt` (spec 6.1). */
  oldestObservedAt: string | null;
};

/* ------------------------------------------------------------------ *
 * Constants — the panel's pixel contract, shared with the CSS in Wave 2
 * ------------------------------------------------------------------ */

/** Panel width, matching the history morph (globals.css `.history-morph`). */
export const EXPANDED_WIDTH = 380;
/** Lines shown before the "Show all {n}" expander. */
export const MAX_VISIBLE_ROWS = 4;
/**
 * How many OTHER retailers the list shows before the expander.
 *
 * The primary offer is not in the list any more — it is the ink pick line at
 * the foot of the panel, which is the pill itself, morphed in place. It still
 * costs one of the panel's lines, so the window for the rest is one short of
 * `MAX_VISIBLE_ROWS` and the panel opens at exactly the height it always did.
 */
export const MAX_VISIBLE_OTHER_ROWS = MAX_VISIBLE_ROWS - 1;
/** One offer row: favicon + name + badge + price. */
export const ROW_HEIGHT = 56;
/** "Back" + "Where to buy" header strip. */
export const HEADER_H = 44;
/** The "Show all {n}" strip. Lives OUTSIDE the row list. */
export const EXPANDER_H = 30;
/** Affiliate disclosure footer. */
export const FOOTER_H = 30;
/** The 1px border on `.offers-morph.is-open`, top + bottom. box-sizing is
 *  border-box, so the height below has to carry it or the last row loses its
 *  bottom two pixels — which is exactly the kind of 2px clipping that used to
 *  hide behind the list's `overflow-y: auto`. */
export const PANEL_BORDER_H = 2;
/** Below this confidence the price is a hedge ("around $X"), not a claim.
 *  Exactly at the threshold counts as confident. */
export const CONFIDENCE_APPROX_THRESHOLD = 0.5;

/* ------------------------------------------------------------------ *
 * Constants — the honesty rules (spec 6.1, 6.2)
 * ------------------------------------------------------------------ */

/** A typical price is only a typical price from this many daily points; the
 *  SQL emits none below it and this side refuses one anyway. */
export const TYPICAL_PRICE_MIN_POINTS = 20;
/** The badge renders when `price <= BELOW_USUAL_MAX_RATIO * typical`. */
export const BELOW_USUAL_MAX_RATIO = 0.9;
/** The badge never says less than this. */
export const BELOW_USUAL_MIN_PERCENT = 10;
/** A merchant's was-price above this multiple of the typical price is not
 *  rendered: observation outranks the merchant's reference price (6.2.5). */
export const WAS_PRICE_MAX_TYPICAL_RATIO = 1.15;
/** Every non-Amazon row shows its absolute stamp once its price is older than
 *  this. An Amazon price always shows its stamp (priceStampFor). */
export const PRICE_STAMP_AFTER_MS = 24 * 60 * 60 * 1000;

/**
 * The market's primary zone for the absolute "as of" stamps, so the server
 * and the client render the same string whatever the visitor's clock says
 * (spec 6.1.2). The four markets in scope first; the rest so a stamp on any
 * page is still local time rather than UTC.
 */
export const MARKET_TIMEZONE: Readonly<Record<RegionCode, string>> = Object.freeze({
  [RegionCode.Canada]: 'America/Toronto',
  [RegionCode.UK]: 'Europe/London',
  [RegionCode.USA]: 'America/New_York',
  [RegionCode.France]: 'Europe/Paris',
  [RegionCode.Australia]: 'Australia/Sydney',
  [RegionCode.Brazil]: 'America/Sao_Paulo',
  [RegionCode.Portugal]: 'Europe/Lisbon',
  [RegionCode.Germany]: 'Europe/Berlin',
  [RegionCode.Spain]: 'Europe/Madrid',
  [RegionCode.Italy]: 'Europe/Rome',
  [RegionCode.Mexico]: 'America/Mexico_City',
  [RegionCode.Switzerland]: 'Europe/Zurich',
  [RegionCode.Austria]: 'Europe/Vienna',
  [RegionCode.Belgium]: 'Europe/Brussels',
  [RegionCode.Netherlands]: 'Europe/Amsterdam',
  [RegionCode.Poland]: 'Europe/Warsaw',
  [RegionCode.Sweden]: 'Europe/Stockholm',
  [RegionCode.Ireland]: 'Europe/Dublin',
  [RegionCode.NewZealand]: 'Pacific/Auckland',
  [RegionCode.India]: 'Asia/Kolkata',
  [RegionCode.Argentina]: 'America/Argentina/Buenos_Aires',
  [RegionCode.Colombia]: 'America/Bogota',
  [RegionCode.Chile]: 'America/Santiago',
  [RegionCode.Japan]: 'Asia/Tokyo',
  [RegionCode.All]: 'UTC',
});

/** Every Amazon marketplace host we know about, from the shared enum — no
 *  hand-rolled /amazon\./ regex, so a new marketplace only has to be added
 *  once, in `shared/src/types/amazon.type.ts`. */
const AMAZON_HOSTS: readonly string[] = Object.values(AmazonDomain);

/* ------------------------------------------------------------------ *
 * Small pure helpers
 * ------------------------------------------------------------------ */

/**
 * Lower-cases a merchant domain and strips scheme, path, query and a leading
 * `www.` so `https://WWW.BestBuy.ca/x?y` and `bestbuy.ca` dedupe together.
 * Returns '' for anything unusable.
 */
export const normalizeMerchantDomain = (raw?: string): string => {
  if (typeof raw !== 'string') return '';
  let domain = raw.trim().toLowerCase();
  if (!domain) return '';
  domain = domain.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  domain = domain.split('/')[0].split('?')[0].split('#')[0];
  domain = domain.replace(/^www\./, '');
  domain = domain.replace(/\.+$/, '');
  return domain;
};

/**
 * True when the row is an Amazon marketplace listing. Matches the marketplace
 * host itself or any subdomain of it (`smile.amazon.com`), never a lookalike
 * (`notamazon.com`, `amazon.com.evil.net`).
 */
export const isAmazonOffer = (offer: { merchantDomain?: string } | null | undefined): boolean => {
  const domain = normalizeMerchantDomain(offer?.merchantDomain);
  if (!domain) return false;
  return AMAZON_HOSTS.some((host) => domain === host || domain.endsWith(`.${host}`));
};

/**
 * A stable hue (0-359) for a merchant domain, for the monogram a retailer gets
 * when it publishes no usable favicon (mec.ca and costco.com, today).
 *
 * Deterministic and cheap: the same store is the same colour on every page and
 * in every session, which is the only property that makes a monogram read as a
 * mark instead of as a random swatch. FNV-1a-ish; the exact mixing does not
 * matter, only that it is stable and spreads neighbouring names apart.
 */
export const brandHue = (domain: string): number => {
  let hash = 0;
  for (let i = 0; i < domain.length; i += 1) {
    hash = (hash * 31 + domain.charCodeAt(i)) % 360000;
  }
  return hash % 360;
};

const isOutOfStock = (offer: { availability?: OfferAvailability }): boolean =>
  offer.availability === 'out_of_stock';

const priceValue = (offer: { price?: OfferPrice }): number | undefined => {
  const value = offer.price?.value;
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

/** Priced rows sort before priceless ones; otherwise ascending. */
const comparePrice = (a: { price?: OfferPrice }, b: { price?: OfferPrice }): number => {
  const pa = priceValue(a);
  const pb = priceValue(b);
  if (pa === undefined && pb === undefined) return 0;
  if (pa === undefined) return 1;
  if (pb === undefined) return -1;
  return pa - pb;
};

/** A row that may be recommended: not an information link, not a list,
 *  member or hidden price (shown but never recommended, spec 6.4.3, 6.5), and
 *  not a row below the confidence floor. That last one is the box's
 *  "nobody could confirm this listing is the product" (stored at 0.45 since
 *  2026-09-19): it is listed with an approximate price and must never be
 *  "Buy Now". Without this, a page whose only rows were such listings made the
 *  cheapest of them the main button. A row with no stored confidence resolves
 *  to the default and passes; so does a synthesized search row, which only the
 *  fallback builders make, and they choose their own primary. */
const isPickableRow = (offer: {
  kind?: OfferKind;
  priceKind?: OfferPriceKind;
  confidence?: number | null;
  source?: ProductOffer['source'];
}): boolean =>
  offer.kind !== 'info' &&
  !isLabelIneligibleKind(offer) &&
  resolveOfferConfidence({ confidence: offer.confidence ?? undefined, source: offer.source ?? 'find-offers' }) >=
    CONFIDENCE_APPROX_THRESHOLD;

const parseIso = (iso: unknown): number | null => {
  if (typeof iso !== 'string' || !iso.trim()) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

/**
 * When the row's price was observed: the source's own `observedAt` when it
 * parses, else the success clock `verifiedAt`, else null (spec 6.1.6). The
 * string is returned verbatim so the caller can print exactly what it holds.
 */
export const offerObservedAt = (
  offer: { observedAt?: string; verifiedAt?: string } | null | undefined,
): string | null => {
  if (!offer) return null;
  if (parseIso(offer.observedAt) !== null) return offer.observedAt as string;
  if (parseIso(offer.verifiedAt) !== null) return offer.verifiedAt as string;
  return null;
};

/**
 * The typical price this row may be judged against, in major units, or
 * undefined: same currency as the price, positive, and resting on at least
 * TYPICAL_PRICE_MIN_POINTS daily observations. The SQL already withholds the
 * median below 20 points; this side refuses one anyway, so a preview or a
 * hand-built row can never earn a badge on thin data.
 */
export const usableTypicalPrice = (
  offer: Pick<ProductOffer, 'price' | 'typicalPrice' | 'typicalPricePoints'>,
): number | undefined => {
  const typical = offer.typicalPrice?.value;
  if (typeof typical !== 'number' || !Number.isFinite(typical) || typical <= 0) return undefined;
  if (
    typeof offer.typicalPricePoints !== 'number' ||
    !Number.isFinite(offer.typicalPricePoints) ||
    offer.typicalPricePoints < TYPICAL_PRICE_MIN_POINTS
  ) {
    return undefined;
  }
  if (offer.price?.currency && offer.typicalPrice?.currency !== offer.price.currency) return undefined;
  return typical;
};

/** Major units back to whole cents, the unit every stored price is in. The
 *  honesty rules compare in cents so a boundary case ("exactly 90%", "exactly
 *  1.15x") cannot fall on the wrong side of a floating-point product. */
const toCents = (major: number): number => Math.round(major * 100);

/**
 * The honesty badge (spec 6.2): `round((1 - price / typical) * 100)` when the
 * price is at or under BELOW_USUAL_MAX_RATIO of the typical price, and never
 * below BELOW_USUAL_MIN_PERCENT. Eligible rows only: a real listing with a
 * sale or regular price, not out of stock, same currency. Undefined means
 * "no badge", never 0.
 *
 * Integer arithmetic on cents: `price * 10 <= typical * 9` is the 90% rule
 * exactly, and the percentage is rounded half up from an exact quotient.
 */
export const belowUsualPercent = (
  offer: { kind?: OfferKind } & Pick<
    ProductOffer,
    'priceKind' | 'availability' | 'price' | 'typicalPrice' | 'typicalPricePoints'
  >,
): number | undefined => {
  if (offer.kind === 'info' || offer.kind === 'search') return undefined;
  if (offer.priceKind !== undefined && offer.priceKind !== 'sale' && offer.priceKind !== 'regular') {
    return undefined;
  }
  if (isOutOfStock(offer)) return undefined;
  const price = priceValue(offer);
  const typical = usableTypicalPrice(offer);
  if (price === undefined || price <= 0 || typical === undefined) return undefined;
  const priceCents = toCents(price);
  const typicalCents = toCents(typical);
  if (priceCents <= 0 || typicalCents <= 0) return undefined;
  if (priceCents * 10 > typicalCents * 9) return undefined;
  const percent = Math.round(((typicalCents - priceCents) * 100) / typicalCents);
  return percent >= BELOW_USUAL_MIN_PERCENT ? percent : undefined;
};

/**
 * Spec 6.2.5: a merchant's was-price above WAS_PRICE_MAX_TYPICAL_RATIO of the
 * price this store has actually charged (the 60-day median) is not rendered.
 * Exactly 1.15x is still rendered; the comparison is in cents.
 */
export const isWasPriceBeyondTypical = (
  offer: Pick<ProductOffer, 'price' | 'originalPrice' | 'typicalPrice' | 'typicalPricePoints'>,
): boolean => {
  const typical = usableTypicalPrice(offer);
  const original = offer.originalPrice?.value;
  if (typical === undefined || typeof original !== 'number' || !Number.isFinite(original)) return false;
  return toCents(original) * 100 > toCents(typical) * Math.round(WAS_PRICE_MAX_TYPICAL_RATIO * 100);
};

/**
 * A deal is "live" only this long after its price was observed. The card's
 * own listing price can be months old (QA 2026-09-28: AirPods Pro 3 "-18%
 * $308.99 Live Deal", last read from Amazon on 2026-08-02, while amazon.ca
 * charged $362.98).
 */
export const LIVE_DEAL_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * When the card's own listing price was observed: the offers row of the same
 * store as the card's link (the Amazon row the Creators refresh files for an
 * Amazon listing, or the row withOwnLinkPrice read a shop's price from),
 * carrying that same price. Null when no row vouches for it: the price then
 * says nothing about today, and no deal may be claimed on it.
 */
export const listingPriceObservedAt = (product: {
  price?: { listingPrice?: number | null } | null;
  externalLinks?: { href?: string | null; type?: string | null } | null;
  offers?: ReadonlyArray<Pick<ProductOffer, 'merchantDomain' | 'kind' | 'price' | 'observedAt' | 'verifiedAt'>> | null;
}): string | null => {
  const listing = product?.price?.listingPrice;
  const href = product?.externalLinks?.href;
  if (typeof listing !== 'number' || !(listing > 0) || !href || product?.externalLinks?.type === 'amazon-search') return null;
  const store = normalizeMerchantDomain(href);
  if (!store) return null;
  const row = (product.offers ?? []).find(
    (offer) =>
      !!offer &&
      offer.kind !== 'info' &&
      normalizeMerchantDomain(offer.merchantDomain) === store &&
      typeof offer.price?.value === 'number' &&
      toCents(offer.price.value) === toCents(listing),
  );
  return row ? offerObservedAt(row) : null;
};

/** The two "as of" stamps of spec 6.1. */
export type PriceStamp = 'amazon' | 'price';

/**
 * Which absolute stamp a row shows, if any (spec 6.1.2, 6.1.3): an Amazon
 * price always, every other listing past 24 hours. Search rows and
 * information links have no price to stamp. `now` is injectable for tests.
 *
 * Always, for Amazon (2026-10-03): its licence asks for "a date/time stamp
 * adjacent to your display of pricing" whenever the prices shown are
 * refreshed less often than hourly (Associates IP License 2(c)(i)), and the
 * Creators refresh runs every 6 to 24 hours. The old rule (a stamp only past
 * 60 minutes) read that clause as being about one price's age.
 */
export const priceStampFor = (
  offer: ProductOffer | PreparedOffer,
  now: number = Date.now(),
): PriceStamp | null => {
  if (offer.kind === 'info' || offer.kind === 'search') return null;
  const observed = parseIso(offerObservedAt(offer));
  if (observed === null) return null;
  if (isAmazonOffer(offer)) return 'amazon';
  return now - observed > PRICE_STAMP_AFTER_MS ? 'price' : null;
};

/**
 * Absolute time for the stamps, in the MARKET's zone so server and client
 * print the same string: `Intl.DateTimeFormat(languageTag, {month: 'short',
 * day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
 * timeZone})`. The zone is named ("EDT", "HAE") as in Amazon's own examples
 * ("as of 14:11 EST"), since a reader may not be in the market's zone. Falls
 * back to English, then to UTC, and never throws; '' for an unparseable input.
 */
export const formatAbsoluteObservedAt = (
  iso: string | null | undefined,
  languageTag: string,
  regionCode: RegionCode,
): string => {
  const ms = parseIso(iso);
  if (ms === null) return '';
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: MARKET_TIMEZONE[regionCode] ?? 'UTC',
  };
  const attempts: Array<[string, Intl.DateTimeFormatOptions]> = [
    [languageTag, options],
    ['en', options],
    ['en', { ...options, timeZone: 'UTC' }],
  ];
  for (const [tag, opts] of attempts) {
    try {
      return new Intl.DateTimeFormat(tag, opts).format(new Date(ms));
    } catch {
      // A bad language tag or an unknown zone: try the next rung.
    }
  }
  return '';
};

/**
 * The row facts a /go href carries (spec 5.3.2): the opaque tracking token as
 * `t=` and the row id as `o=`, both copied as strings and never parsed here
 * (`buildOutboundHref` shape-checks them). An information link, an Amazon row
 * and a synthesized search row never carry `t`: their rows hold no token, and
 * this keeps that true even if one ever arrived with one. The id rides along
 * whenever the row has one, so every click joins back to its row.
 */
export const offerOutboundContext = (offer: {
  kind?: OfferKind;
  merchantDomain?: string;
  trackingToken?: string;
  offerId?: string;
}): { trackingToken?: string; offerId?: string } => ({
  ...(offer.trackingToken &&
  offer.kind !== 'info' &&
  offer.kind !== 'search' &&
  !isAmazonOffer(offer)
    ? { trackingToken: offer.trackingToken }
    : {}),
  ...(offer.offerId ? { offerId: offer.offerId } : {}),
});

/** eBay's hosts, every market (ebay.ca, ebay.co.uk, ebay.com, ebay.fr). */
const EBAY_HOST_RE = /(^|\.)ebay\.[a-z.]+$/;

/**
 * The name a row prints. A marketplace listing on eBay is "eBay" whatever the
 * row stores (spec 6.7): the seller behind the listing is never named. Every
 * other row uses its own name, then the reviewed-merchant registry's, and the
 * caller falls back to the host.
 */
const displayMerchantName = (offer: ProductOffer, domainKey: string): string => {
  if (offer.merchantKind === 'marketplace' && EBAY_HOST_RE.test(domainKey)) return 'eBay';
  return offer.merchantName?.trim() || lookupOfferMerchant(domainKey)?.name || '';
};

/** A deal chip is a claim about the future: only while `endsAt` is ahead of
 *  `now` (spec 6.3). A deal with no end date is not rendered either; the row
 *  is refreshed by the cron and the marker cleared when it lapses. */
export const isDealActive = (
  offer: { deal?: OfferDeal } | null | undefined,
  now: number = Date.now(),
): boolean => {
  const ends = parseIso(offer?.deal?.endsAt);
  return ends !== null && ends > now;
};

/**
 * Panel height for `rowCount` rows — the exact sum of what is inside it, with
 * no ceiling.
 *
 * There used to be a `PANEL_MAX_H` clamp here and an `overflow-y: auto` on the
 * row list to absorb it. A shelf of two retailers is not a scroll region: the
 * scroll port existed only for the clamp, it hid the last row's bottom edge
 * behind the footer, and on a trackpad it swallowed the page scroll the reader
 * meant to give the card. The panel now grows to fit instead, so the reader
 * always sees every row the expander promised — the default window is still
 * `MAX_VISIBLE_OTHER_ROWS`, so growth only happens on a deliberate "Show all {n}".
 *
 * WHAT THIS NUMBER IS NOT. It is only the CONSTANT part of the panel: border,
 * header, the other retailers' rows and the expander. The two pieces below them
 * are measured at open time and added in CSS —
 *
 *     height: calc(<panelHeight>px + var(--offers-pick-h) + var(--offers-gap))
 *
 * — because neither can be known here. `--offers-pick-h` is the pill's own
 * height, which is what the ink pick line has to be so it lands on the pill's
 * exact pixels; `--offers-gap` is the distance from the pill's bottom to the
 * buy column's, i.e. the price block, which is two lines on a discounted
 * product and one on a plain one. A constant `FOOTER_H` here drifted ~20px
 * between those two cases and put the pick line a visible step off the button
 * it is supposed to replace. The disclosure strip lives in that gap instead and
 * stretches to fill it, so the arithmetic closes for every product.
 */
export const panelHeight = (rowCount: number, showExpander = false): number => {
  const rows = Number.isFinite(rowCount) ? Math.max(0, Math.floor(rowCount)) : 0;
  return (
    PANEL_BORDER_H + HEADER_H + rows * ROW_HEIGHT + (showExpander ? EXPANDER_H : 0)
  );
};

/**
 * Relative time for the "Prices checked {time}" claim (sr-only since 2026-08).
 *
 * Same ladder and locale handling as the resume-history chip
 * (`components/search-bar/text-search/resume-history-chip.tsx`): minutes under
 * an hour, hours under a day, days under a week, then weeks — with
 * `numeric: 'auto'` so English says "yesterday" rather than "1 day ago", and an
 * `en` fallback for language tags Intl rejects.
 *
 * NEVER throws: a missing, malformed or non-string timestamp returns '' and the
 * caller simply omits the freshness line.
 */
export const formatObservedAt = (iso: string | null | undefined, languageCode: string): string => {
  if (typeof iso !== 'string' || !iso.trim()) return '';
  try {
    const timestamp = Date.parse(iso);
    if (!Number.isFinite(timestamp)) return '';

    const diffMin = Math.round((timestamp - Date.now()) / 60_000);
    let rtf: Intl.RelativeTimeFormat;
    try {
      rtf = new Intl.RelativeTimeFormat(languageCode, { numeric: 'auto' });
    } catch {
      rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    }
    if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
    const diffHr = Math.round(diffMin / 60);
    if (Math.abs(diffHr) < 24) return rtf.format(diffHr, 'hour');
    const diffDay = Math.round(diffHr / 24);
    if (Math.abs(diffDay) < 7) return rtf.format(diffDay, 'day');
    return rtf.format(Math.round(diffDay / 7), 'week');
  } catch {
    return '';
  }
};

/* ------------------------------------------------------------------ *
 * pickPrimaryOffer
 * ------------------------------------------------------------------ */

/**
 * The offer behind the pill's MAIN "Buy Now" zone.
 *
 * bestPlace ?? Amazon ?? cheapest in-stock ?? first, over the rows a reader
 * can act on. An information link is never the pick, even when every priced
 * row is out of stock (spec rule R39), and neither is a list, member or hidden
 * price (spec 6.4.3). `null` when no row qualifies: the callers then keep the
 * single-link CTA rather than put "Buy Now" on a price nobody pays.
 *
 * Because `prepareOffers` sorts out-of-stock rows last, an out-of-stock row can
 * only win the first two steps when it is the ONLY bestPlace / Amazon row —
 * deliberate: the offer still exists, and the row renders muted.
 */
export const pickPrimaryOffer = <T extends PreparedOffer>(rows: readonly T[]): T | null => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const candidates = rows.filter((row) => isPickableRow(row));
  if (candidates.length === 0) return null;

  const bestPlace = candidates.find((row) => row.isBestPlace === true || row.badge === 'bestPlace');
  if (bestPlace) return bestPlace;

  const amazon = candidates.find((row) => isAmazonOffer(row));
  if (amazon) return amazon;

  const cheapestInStock = candidates
    .filter((row) => !isOutOfStock(row) && priceValue(row) !== undefined)
    .sort(comparePrice)[0];
  if (cheapestInStock) return cheapestInStock;

  return candidates[0];
};

/* ------------------------------------------------------------------ *
 * prepareOffers
 * ------------------------------------------------------------------ */

const warnOnce = (message: string): void => {
  // Dev-only: a cross-currency row reaching the UI means a writer wrote the
  // wrong region, which is worth seeing locally but never worth a prod log line.
  if (process.env.NODE_ENV !== 'production') console.warn(message);
};

/**
 * Filters, dedupes, sorts and annotates raw offers for one market.
 *
 * Pipeline, in order:
 *  1. drop structurally unusable rows (no merchantDomain, no url);
 *  2. drop rows priced in another currency — a CAD page listing a USD offer
 *     misleads, and "best price" is only meaningful within one market;
 *  3. drop Amazon rows when `suppressAmazon`;
 *  4. dedupe by merchantDomain, keeping the cheapest row per merchant;
 *  5. sort by presentation tier (real prices, list prices, hidden prices, out
 *     of stock, information links last), then bestPlace → bestPrice → Amazon
 *     → price ascending;
 *  6. pin the Amazon row into the visible window if sorting pushed it out;
 *  7. annotate badge / isApproximate / savings / the honesty badge / labels.
 *
 * Every field of the incoming row rides through untouched, `trackingToken`
 * and `offerId` included: they are what the /go href is built from.
 */
export const prepareOffers = (
  offers: readonly ProductOffer[] | null | undefined,
  { regionCode, localePath, suppressAmazon = false }: PrepareOffersOptions,
): PreparedOffers => {
  const empty: PreparedOffers = {
    rows: [],
    hiddenCount: 0,
    primary: null,
    freshestObservedAt: null,
    oldestObservedAt: null,
  };
  if (!Array.isArray(offers) || offers.length === 0) return empty;

  const expectedCurrency = getCurrencyCode(regionCode);

  /* 1-3. filters ---------------------------------------------------- */
  const kept: Array<{ offer: ProductOffer; domainKey: string; order: number }> = [];
  offers.forEach((offer, order) => {
    if (!offer || typeof offer !== 'object') return;
    const domainKey = normalizeMerchantDomain(offer.merchantDomain);
    if (!domainKey) return;
    if (typeof offer.url !== 'string' || !offer.url.trim()) return;

    const currency = offer.price?.currency ?? offer.priceRange?.currency;
    if (typeof currency === 'string' && currency.toUpperCase() !== expectedCurrency.toUpperCase()) {
      warnOnce(
        `[offers] dropped ${domainKey}: ${currency} offer on a ${expectedCurrency} (${regionCode}) page`,
      );
      return;
    }

    // A row whose page is no product page (a PDF, a manual, a pro portal) or
    // whose store does not serve this market (shopee.ph, or amazon.com on an
    // en-CA page) is never a place to buy, let alone "Our pick". Information
    // rows sell nothing, so only the first rule holds them.
    if (
      buyLinkProblem(offer.url, {
        regionCode,
        info: offer.kind === 'info',
        evidence: offerStoreEvidence(offer, expectedCurrency),
      })
    ) {
      return;
    }

    if (suppressAmazon && isAmazonOffer(offer)) return;

    kept.push({ offer, domainKey, order });
  });

  /* 4. dedupe by merchant ------------------------------------------- */
  // One row per merchant, cheapest wins. The "best place" / "best price" flags
  // are OR-ed onto the survivor so a cheaper duplicate never silently strips a
  // badge that a writer put on the pricier row.
  const byDomain = new Map<string, { offer: ProductOffer; domainKey: string; order: number }>();
  for (const entry of kept) {
    const incumbent = byDomain.get(entry.domainKey);
    if (!incumbent) {
      byDomain.set(entry.domainKey, { ...entry, offer: { ...entry.offer } });
      continue;
    }

    const winner = (() => {
      const priceDelta = comparePrice(entry.offer, incumbent.offer);
      if (priceDelta !== 0) return priceDelta < 0 ? entry : incumbent;
      const confDelta = (entry.offer.confidence ?? 0) - (incumbent.offer.confidence ?? 0);
      if (confDelta !== 0) return confDelta > 0 ? entry : incumbent;
      return incumbent; // stable: first occurrence wins
    })();

    byDomain.set(entry.domainKey, {
      ...winner,
      order: Math.min(entry.order, incumbent.order),
      offer: {
        ...winner.offer,
        isBestPlace: entry.offer.isBestPlace === true || incumbent.offer.isBestPlace === true,
        isBestPrice: entry.offer.isBestPrice === true || incumbent.offer.isBestPrice === true,
      },
    });
  }

  /* 5. sort ---------------------------------------------------------- */
  const tierOf = (offer: ProductOffer): number => {
    if (offer.isBestPlace === true) return 0;
    if (offer.isBestPrice === true) return 1;
    if (isAmazonOffer(offer)) return 2;
    return 3;
  };

  const sorted = [...byDomain.values()].sort((a, b) => {
    // The presentation tier comes first, whatever else a row claims to be:
    // real prices, then list prices, then hidden prices, then out of stock,
    // then information links. Shared with the ranker so the two never disagree.
    const presentationDelta = offerPresentationTier(a.offer) - offerPresentationTier(b.offer);
    if (presentationDelta !== 0) return presentationDelta;

    const tierDelta = tierOf(a.offer) - tierOf(b.offer);
    if (tierDelta !== 0) return tierDelta;

    const priceDelta = comparePrice(a.offer, b.offer);
    if (priceDelta !== 0) return priceDelta;

    // Final tiebreaks keep the output byte-identical for identical input.
    if (a.domainKey !== b.domainKey) return a.domainKey < b.domainKey ? -1 : 1;
    return a.order - b.order;
  });

  /* 6. pin Amazon into the visible window ---------------------------- */
  // Amazon is where most of our readers actually complete a purchase, so it
  // must never fall off the first screen of the panel. An OUT-OF-STOCK Amazon
  // row is deliberately not pinned: "out of stock last" outranks this rule.
  //
  // With well-formed flags the Amazon tier already lands it at index <= 2, so
  // this only fires when several rows carry isBestPlace/isBestPrice at once —
  // which happens, because those flags are written by three independent writers
  // and go stale between refreshes.
  const amazonIndex = sorted.findIndex((entry) => isAmazonOffer(entry.offer));
  if (amazonIndex > MAX_VISIBLE_ROWS - 1 && !isOutOfStock(sorted[amazonIndex].offer)) {
    const [amazonEntry] = sorted.splice(amazonIndex, 1);
    sorted.splice(MAX_VISIBLE_ROWS - 1, 0, amazonEntry);
  }

  /* 7. annotate ------------------------------------------------------ */
  let bestPlaceTaken = false;
  let bestPriceTaken = false;

  const rows: PreparedOffer[] = sorted.map(({ offer, domainKey }) => {
    // An information link or a list / member / hidden price never carries a
    // label, whatever a stale flag on the wire says (spec 6.4.3, 6.4.6).
    let badge: OfferBadge | null = null;
    if (isPickableRow(offer)) {
      if (offer.isBestPlace === true && !bestPlaceTaken) {
        badge = 'bestPlace';
        bestPlaceTaken = true;
      } else if (offer.isBestPrice === true && !bestPriceTaken) {
        badge = 'bestPrice';
        bestPriceTaken = true;
      }
    }

    const price = priceValue(offer);
    const original = offer.originalPrice?.value;
    const typical = usableTypicalPrice(offer);
    // Merchant reference prices are subordinate to observation (6.2.5): a
    // was-price far above what this store has actually charged for two months
    // is not rendered, and the badge (if earned) is what the page asserts.
    const suppressWasPrice = isWasPriceBeyondTypical(offer);
    const hasComparableOriginal =
      typeof original === 'number' &&
      Number.isFinite(original) &&
      price !== undefined &&
      original > price &&
      (offer.originalPrice?.currency ?? offer.price?.currency) === offer.price?.currency &&
      !suppressWasPrice;

    const saveAmount = hasComparableOriginal ? original - price : undefined;
    const savePercent =
      saveAmount !== undefined && original! > 0 ? Math.round((saveAmount / original!) * 100) : undefined;
    const belowUsual = belowUsualPercent(offer);

    return {
      ...offer,
      domainKey,
      // A row with no `merchantName` used to print its bare domain — "mec.ca"
      // sitting under a "Where to buy" header that names Amazon properly. The
      // reviewed-merchant registry already holds the human name for every
      // domain we are willing to link to, so it answers first and the domain
      // stays as the last resort.
      merchantName: displayMerchantName(offer, domainKey),
      badge,
      // ONE resolver, shared with the ranker. This used to read
      // `typeof offer.confidence === 'number'`, so a null confidence rendered as
      // a hard price here while the ranker scored the same row 0 and refused to
      // label it — too dubious to recommend, solid enough to quote. See
      // DEFAULT_OFFER_CONFIDENCE in rank-offers.ts.
      isApproximate: resolveOfferConfidence(offer) < CONFIDENCE_APPROX_THRESHOLD,
      priceLabel: price === undefined ? '' : priceByCountry(price, regionCode, localePath),
      originalPriceLabel: hasComparableOriginal ? priceByCountry(original, regionCode, localePath) : '',
      saveAmount,
      savePercent,
      saveLabel: saveAmount === undefined ? '' : priceByCountry(saveAmount, regionCode, localePath),
      ...(belowUsual !== undefined ? { belowUsualPercent: belowUsual } : {}),
      typicalPriceLabel: typical === undefined ? '' : priceByCountry(typical, regionCode, localePath),
    };
  });

  /* freshness bounds ------------------------------------------------- */
  // Over the rows that carry a price we observed: an information link has no
  // observation to bound the claim with (spec 6.1.1, 6.1.6).
  let freshestObservedAt: string | null = null;
  let oldestObservedAt: string | null = null;
  let freshestMs = -Infinity;
  let oldestMs = Infinity;
  for (const row of rows) {
    if (isInfoOffer(row)) continue;
    const observed = offerObservedAt(row);
    if (observed === null) continue;
    const ms = Date.parse(observed);
    if (ms > freshestMs) {
      freshestMs = ms;
      freshestObservedAt = observed;
    }
    if (ms < oldestMs) {
      oldestMs = ms;
      oldestObservedAt = observed;
    }
  }

  return {
    rows,
    hiddenCount: Math.max(0, rows.length - MAX_VISIBLE_ROWS),
    primary: pickPrimaryOffer(rows),
    freshestObservedAt,
    oldestObservedAt,
  };
};

/* ------------------------------------------------------------------ *
 * The two-option fallback
 * ------------------------------------------------------------------ */

/**
 * The same panel, for the pages the Offers Engine has not reached yet.
 *
 * Most product cards still carry exactly one retailer link plus the Amazon
 * search fallback for the same product — which is two buying options, i.e. the
 * thing this panel exists to present. Until 2026-08 those pages got a separate
 * widget instead: a pill that swapped itself, ON HOVER, for two competing
 * buttons anchored to a different edge than everything else on the card. Same
 * decision, second design language, and a hover trap on the way in. This
 * builder feeds the panel instead, so a reader meets ONE "where to buy"
 * surface whatever the data behind the card looks like.
 *
 * The rows are synthesized, not observed, so the honesty rules are stricter
 * than in `prepareOffers`:
 *   · the partner row carries the price the card is already displaying (it is
 *     that link's price) and nothing else — no was-price, no "best price";
 *   · the partner row is "Our pick" only with evidence that its store sells
 *     the product in this market (linkSellsInMarket: a price in the market's
 *     currency read on that store, or a reviewed merchant of the market), or
 *     when it is the store of a brand sold only on its own site. Without it,
 *     a store's product page is still where the CTA goes but claims no pick,
 *     and the maker's own page is an information row ("Official site"),
 *     never counted and never the pick (2026-09-28: phonak.com's product
 *     information page and jabraenhance.com were "Our pick" on Canadian
 *     pages);
 *   · the Amazon row is `kind: 'search'`, with NO price. It is a search, and
 *     the row says so rather than implying we priced it;
 *   · neither row claims stock, because nothing here observed any.
 */
export const prepareLinkOffers = (input: {
  /** The partner retailer's product URL. */
  partnerUrl: string;
  /** Display name for the partner; falls back to the registry, then the host. */
  partnerName?: string;
  /** The listing price the card already shows, in MAJOR units. */
  price?: number;
  /** Amazon search URL for the same product, when the page has one. */
  amazonSearchUrl?: string;
  regionCode: RegionCode;
  /** The page's locale path, for the price label (see PrepareOffersOptions). */
  localePath?: string;
  /** The product the card is for, with the offers read for it: the evidence
   *  that its link's store sells here. */
  product?: { title?: string | null; brandName?: string | null; offers?: readonly ProductOffer[] | null };
  /** The product's purchase channel (purchase-channel.ts). */
  channel?: PurchaseChannel;
}): PreparedOffers => {
  const { partnerUrl, partnerName, price, amazonSearchUrl, regionCode, localePath, product, channel } = input;

  const row = (
    url: string,
    kind: OfferKind,
    name?: string,
    value?: number,
    badge: OfferBadge | null = null,
  ): PreparedOffer | null => {
    const domainKey = normalizeMerchantDomain(url);
    if (!domainKey || typeof url !== 'string' || !url.trim()) return null;
    const priced = kind === 'offer' && value !== undefined && Number.isFinite(value);
    return {
      merchantDomain: domainKey,
      merchantName: name?.trim() || lookupOfferMerchant(domainKey)?.name || domainKey,
      url,
      // Provenance: the product's own link record, not an offers writer.
      source: 'find-product-candidate',
      kind,
      domainKey,
      badge,
      isApproximate: false,
      ...(priced ? { price: { value: value!, currency: getCurrencyCode(regionCode) } } : {}),
      priceLabel: priced ? priceByCountry(value!, regionCode, localePath) : '',
      originalPriceLabel: '',
      saveLabel: '',
      typicalPriceLabel: '',
    };
  };

  const dtcStore = channel === 'dtc' && isDtcStoreUrl(partnerUrl);
  const sells =
    dtcStore ||
    linkSellsInMarket(partnerUrl, {
      regionCode,
      offers: product?.offers,
      marketCurrency: getCurrencyCode(regionCode),
      product,
    });
  // A product page of a store serving this market; the card's own link is
  // already held to that by presentProduct.
  const problem = dtcStore ? null : buyLinkProblem(partnerUrl, { regionCode, product, evidence: { pricedInMarket: sells } });
  const partner = problem
    ? null
    : sells
      ? // "Our pick": the partner link IS the pick, it is what the CTA opens.
        row(partnerUrl, 'offer', partnerName, price, 'bestPlace')
      : isMakerLink(partnerUrl, product)
        ? row(partnerUrl, 'info', partnerName)
        : row(partnerUrl, 'offer', partnerName, price);

  const rows = [partner, amazonSearchUrl ? row(amazonSearchUrl, 'search', 'Amazon') : null]
    .filter((entry): entry is PreparedOffer => entry !== null)
    // Information links go last, as in prepareOffers.
    .sort((a, b) => Number(a.kind === 'info') - Number(b.kind === 'info'));

  return {
    rows,
    hiddenCount: 0,
    primary: rows.find((entry) => entry.kind !== 'info') ?? null,
    freshestObservedAt: null,
    oldestObservedAt: null,
  };
};

/* ------------------------------------------------------------------ *
 * Clinic and not-in-market products
 * ------------------------------------------------------------------ */

export type ChannelOffers = {
  /** Priced offers from stores in this market, then information links. No
   *  Amazon row and no search row, ever. */
  prepared: PreparedOffers;
  /** The brand's own product page, when the card has one: the notice's
   *  "Official site" link. Never a place to buy. */
  officialSite: PreparedOffer | null;
};

/**
 * What a card may offer when its product is not sold at retail here
 * (purchaseChannel 'clinic' or 'not-in-market', 2026-09-28).
 *
 * A clinic product (a prescription hearing aid) keeps the priced offers the
 * engine read at stores in this market (shop.omnihearing.ca at $2,349 CAD on
 * the behind-the-ear page): those are real sales, and the in-stock one may be
 * the pick. Everything that stood in for a sale goes: the Amazon row, the
 * Amazon search, and any partner link without a price in this market (a
 * maker's page is its "Official site" link). With no priced, in-stock row the
 * card shows a notice instead of a button.
 *
 * A product not sold in this market offers nothing at all.
 *
 * The product page's CTA and its structured data both read this, so the
 * JSON-LD never declares an Offer the card does not show.
 */
export const prepareChannelOffers = (input: {
  channel: PurchaseChannel;
  offers: readonly ProductOffer[] | null | undefined;
  /** The card's own link, after presentProduct. */
  partnerUrl?: string | null;
  partnerName?: string;
  /** The card's own listing price, in MAJOR units. */
  price?: number;
  regionCode: RegionCode;
  localePath?: string;
  product?: { title?: string | null; brandName?: string | null; offers?: readonly ProductOffer[] | null };
}): ChannelOffers => {
  const none: PreparedOffers = { rows: [], hiddenCount: 0, primary: null, freshestObservedAt: null, oldestObservedAt: null };
  if (input.channel !== 'clinic') return { prepared: none, officialSite: null };

  const engine = prepareOffers(input.offers, {
    regionCode: input.regionCode,
    localePath: input.localePath,
    suppressAmazon: true,
  });
  const linkRows =
    input.partnerUrl && !isAmazonOffer({ merchantDomain: normalizeMerchantDomain(input.partnerUrl) })
      ? prepareLinkOffers({
          partnerUrl: input.partnerUrl,
          partnerName: input.partnerName,
          price: input.price,
          regionCode: input.regionCode,
          localePath: input.localePath,
          product: input.product,
          channel: input.channel,
        }).rows
      : [];

  // A real sale only: a priced row the engine read at a store in this market
  // and may recommend, or the card's own store link WITH its market evidence
  // (the "Our pick" rule above) and a price.
  const engineSale = (entry: PreparedOffer) => !!entry.priceLabel && isPickableRow(entry);
  const linkSale = (entry: PreparedOffer) => entry.badge === 'bestPlace' && !!entry.priceLabel;
  const seen = new Set<string>();
  const merged: PreparedOffer[] = [];
  for (const [entries, sale] of [
    [engine.rows, engineSale],
    [linkRows, linkSale],
  ] as const) {
    for (const entry of entries) {
      if (entry.kind === 'search' || seen.has(entry.domainKey)) continue;
      if (entry.kind !== 'info' && !sale(entry)) continue;
      seen.add(entry.domainKey);
      merged.push(entry);
    }
  }
  const rows = [...merged.filter((entry) => entry.kind !== 'info'), ...merged.filter((entry) => entry.kind === 'info')];
  const pick = pickPrimaryOffer(rows);
  const primary = pick && !isOutOfStockOffer(pick) ? pick : null;

  let freshestObservedAt: string | null = null;
  let oldestObservedAt: string | null = null;
  for (const entry of rows) {
    const observed = entry.kind === 'info' ? null : offerObservedAt(entry);
    if (!observed) continue;
    if (!freshestObservedAt || Date.parse(observed) > Date.parse(freshestObservedAt)) freshestObservedAt = observed;
    if (!oldestObservedAt || Date.parse(observed) < Date.parse(oldestObservedAt)) oldestObservedAt = observed;
  }

  return {
    prepared: {
      rows,
      hiddenCount: Math.max(0, rows.length - MAX_VISIBLE_ROWS),
      primary,
      freshestObservedAt,
      oldestObservedAt,
    },
    officialSite: rows.find((entry) => entry.kind === 'info') ?? null,
  };
};

/**
 * The purchase-channel verdict for one presented product in one market, and
 * what it allows: the one entry point every surface that sends a shopper
 * somewhere reads (2026-09-28). The card's CTA (external-link.tsx), its
 * JSON-LD and ItemList (product-schema.tsx, itemlist-schema.tsx), the AI
 * search result and the AI product catalog, the search shelf's previews
 * (search-product-preview.ts) and a kit line (select-kit-product.ts).
 *
 * 'retail' and 'dtc': `offers` is null and the surface keeps its own buy
 * link, the market's Amazon search included (Olivier, 2026-09-21).
 * 'clinic' and 'not-in-market': `offers` is prepareChannelOffers' answer.
 * Its `prepared.primary` is the one real sale (a priced, in-stock offer at a
 * store in this market) or null, and nothing stands in for a sale: no Amazon
 * listing, no Amazon search, no Buy.
 */
export const productChannelOffers = (
  product: {
    title?: string | null;
    brandName?: string | null;
    availability?: { status?: string | null } | null;
    externalLinks?: { href?: string | null; type?: string | null } | null;
    offers?: readonly ProductOffer[] | null;
    price?: { listingPrice?: number | null } | null;
  },
  context: { regionCode: RegionCode; localePath?: string; pagePath?: string | null },
): { channel: PurchaseChannel; offers: ChannelOffers | null } => {
  const channel = purchaseChannel(product, { regionCode: context.regionCode, pagePath: context.pagePath });
  if (channel !== 'clinic' && channel !== 'not-in-market') return { channel, offers: null };
  const link = product.externalLinks;
  const partnerUrl = link?.href && link.type !== 'amazon-search' ? link.href : null;
  return {
    channel,
    offers: prepareChannelOffers({
      channel,
      offers: product.offers,
      partnerUrl,
      partnerName: partnerUrl ? getRetailerName(partnerUrl) || getDisplayDomain(partnerUrl, product) : undefined,
      price: product.price?.listingPrice ?? undefined,
      regionCode: context.regionCode,
      localePath: context.localePath,
      product,
    }),
  };
};

/* ------------------------------------------------------------------ *
 * Out of stock → Amazon
 * ------------------------------------------------------------------ */

/** True when the row's OBSERVED availability is out of stock. Missing or
 *  'unknown' is not out of stock — it is a claim we cannot make either way. */
export const isOutOfStockOffer = (
  offer: { availability?: OfferAvailability } | null | undefined,
): boolean => offer?.availability === 'out_of_stock';

export type AmazonFallbackInput = {
  /** The product's own Amazon listing (a /dp/ URL). Tagged or not — /go re-tags. */
  amazonUrl?: string | null;
  /** An Amazon search for the product: for when no listing is known, or the
   *  listing is itself one of the out-of-stock rows. */
  amazonSearchUrl?: string | null;
  /** The listing price the page already shows, in MAJOR units. Attached to a
   *  /dp/ fallback so the line under the button still names what it opens;
   *  never to a search, which has no price to name. */
  listingPrice?: number | null;
  /** When Amazon's API gave that price (listingPriceObservedAt). An Amazon
   *  price is shown only with its time (Associates IP License 2(c)(i)), so
   *  without one the fallback carries no price. */
  listingObservedAt?: string | null;
  regionCode: RegionCode;
  /** The page's locale path, for the price label (see PrepareOffersOptions). */
  localePath?: string;
  /** App-shell Associates gate: no Amazon destination may be synthesized there. */
  suppressAmazon?: boolean;
};

/**
 * An out-of-stock pick never gets the button; Amazon does.
 *
 * `pickPrimaryOffer` can only land on an out-of-stock row when NO row is
 * purchasable — the ranker labels no dead row, and the cheapest-in-stock rung
 * skips them — so this is the "every store we track is out of stock" case
 * (2026-09: an Arc'teryx page whose two partner rows had both been out of
 * stock for two weeks still put "Buy Now" on the dead one). Sending the
 * reader to an out-of-stock partner page earns nothing and helps nobody;
 * sending them to Amazon at least offers the product's own listing, or a
 * search for it, and the Associates attribution that comes with either.
 *
 * Ladder: an in-stock Amazon row we already have → the product's Amazon
 * listing, when no Amazon row exists to say it is dead too → an Amazon
 * search. The synthesized row becomes the PRIMARY only, never a row in the
 * list: the list stays a record of what we observed (those stores, out of
 * stock) and the button says where to buy instead. A search row is `kind:
 * 'search'` and unpriced, and every surface labels it as a search — it is not
 * an offer, and dressing it as one would be lying in the one place a reader is
 * deciding where to spend money.
 */
export const preferAmazonWhenOutOfStock = (
  prepared: PreparedOffers,
  input: AmazonFallbackInput,
): PreparedOffers => {
  const { primary, rows } = prepared;
  if (!primary || !isOutOfStockOffer(primary) || input.suppressAmazon) return prepared;

  const inStockAmazon = rows.find(
    (row) => isAmazonOffer(row) && row.kind !== 'search' && !isOutOfStockOffer(row) && isPickableRow(row),
  );
  if (inStockAmazon) return { ...prepared, primary: inStockAmazon };

  // An Amazon row in the list is out of stock (or we would have taken it), so
  // its listing is not a place to send anyone; only a search is.
  const amazonRowExists = rows.some((row) => isAmazonOffer(row));
  const listing = !amazonRowExists && input.amazonUrl?.trim() ? input.amazonUrl : null;
  const search = input.amazonSearchUrl?.trim() ? input.amazonSearchUrl : null;
  const url = listing ?? search;
  if (!url) return prepared;
  const kind: OfferKind = listing ? 'offer' : 'search';

  const domainKey = normalizeMerchantDomain(url);
  if (!domainKey) return prepared;

  const observedAt = parseIso(input.listingObservedAt) !== null ? (input.listingObservedAt as string) : undefined;
  const price =
    kind === 'offer' &&
    observedAt !== undefined &&
    typeof input.listingPrice === 'number' &&
    Number.isFinite(input.listingPrice) &&
    input.listingPrice > 0
      ? input.listingPrice
      : undefined;

  const fallback: PreparedOffer = {
    merchantDomain: domainKey,
    // "Amazon", not the registry's "Amazon.ca": every surface prints the
    // wordmark beside it, and the marketplace is the market's, not a choice.
    merchantName: 'Amazon',
    url,
    // Provenance: the product's own link record, not an offers writer.
    source: 'find-product-candidate',
    kind,
    domainKey,
    badge: null,
    isApproximate: false,
    ...(price !== undefined
      ? { price: { value: price, currency: getCurrencyCode(input.regionCode) }, observedAt }
      : {}),
    priceLabel: price !== undefined ? priceByCountry(price, input.regionCode, input.localePath) : '',
    originalPriceLabel: '',
    saveLabel: '',
    typicalPriceLabel: '',
  };
  return { ...prepared, primary: fallback };
};

/* ------------------------------------------------------------------ *
 * Always an Amazon search
 * ------------------------------------------------------------------ */

export type AmazonSearchRowInput = {
  /** An Amazon search for the product (tagged or not: /go re-tags). */
  amazonSearchUrl?: string | null;
  regionCode: RegionCode;
  /** Nothing Amazon may be shown: the app shell, a book or software page, a
   *  brand sold only on its own site. */
  suppressAmazon?: boolean;
  /** An applied verdict said the ASIN's Amazon item is another product
   *  (`product.amazonMatch === 'wrong'`): its Amazon rows are gone. */
  amazonWrong: boolean;
  /** The product has an ASIN (after presentProduct, a wrong one has not). */
  hasAsin: boolean;
};

/**
 * When we could not find the product on Amazon, the panel still offers an
 * Amazon search for it (Olivier, 2026-09-21: "we should always have a search
 * page offer in the offers for amazon if we can't find the right product").
 *
 * Two cases, and only these, so every other panel renders as before:
 *  · the ASIN's item is another product (`amazonWrong`): the search row counts
 *    toward the two-row rule, so a product left with one real shop shows that
 *    shop as the pick beside the Amazon search instead of losing the shop to a
 *    lone "Search Now" button;
 *  · the product has no ASIN: the row joins a panel that already shows (two
 *    rows of its own), never creates one, so single-link pages keep their CTA.
 * A product whose ASIN carries no verdict keeps its own Amazon row (or none).
 *
 * The row is `kind: 'search'`, unpriced and never the pick (the primary is
 * computed before it exists): a search is not an offer, and every surface
 * labels it as one. It goes before the information links, inside the
 * visible window, and never when Amazon is already the pick (the
 * out-of-stock fallback) or already a row.
 */
export const withAmazonSearchRow = (
  prepared: PreparedOffers,
  input: AmazonSearchRowInput,
): PreparedOffers => {
  const url = input.amazonSearchUrl?.trim();
  if (!url || input.suppressAmazon) return prepared;
  if (!input.amazonWrong && input.hasAsin) return prepared;
  const { rows, primary } = prepared;
  // Amazon is already the pick (every store out of stock) or already a row.
  if ((primary && isAmazonOffer(primary)) || rows.some((row) => isAmazonOffer(row))) return prepared;
  const offerRows = countOfferRows(rows);
  if (input.amazonWrong ? offerRows < 1 : offerRows < 2) return prepared;

  const domainKey = normalizeMerchantDomain(url);
  if (!domainKey) return prepared;
  const search: PreparedOffer = {
    merchantDomain: domainKey,
    // "Amazon", not the registry's "Amazon.ca": the marketplace is the market's.
    merchantName: 'Amazon',
    url,
    // Provenance: the product's own link record, not an offers writer.
    source: 'find-product-candidate',
    kind: 'search',
    domainKey,
    badge: null,
    isApproximate: false,
    priceLabel: '',
    originalPriceLabel: '',
    saveLabel: '',
    typicalPriceLabel: '',
  };

  const firstInfo = rows.findIndex((row) => isInfoOffer(row));
  const at = Math.min(firstInfo === -1 ? rows.length : firstInfo, MAX_VISIBLE_ROWS - 1);
  const next = [...rows.slice(0, at), search, ...rows.slice(at)];
  return { ...prepared, rows: next, hiddenCount: Math.max(0, next.length - MAX_VISIBLE_ROWS) };
};
